"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Label,
} from "@headlessui/react";
import { Icon } from "@plura/client/components";
import Tooltip from "../../atoms/Tooltip";

export type SelectOption = {
    id: number;
    name: string;
    description?: string;
    icon?: React.ReactNode;
};

type SelectProps = {
    className?: string;
    classButton?: string;
    classLabel?: string;
    label?: string;
    innerLabel?: string;
    tooltip?: string;
    value: SelectOption | null;
    onChange: (value: SelectOption) => void;
    options: SelectOption[];
    isBlack?: boolean;
    placeholder?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
    emptyMessage?: string;
    disabled?: boolean;
    loading?: boolean;
};

const Select = ({
    className,
    classButton,
    classLabel,
    label,
    innerLabel,
    tooltip,
    value = null,
    onChange,
    options,
    isBlack,
    placeholder,
    searchable = false,
    searchPlaceholder = "Search…",
    emptyMessage = "No results found",
    disabled = false,
    loading = false,
}: SelectProps) => {
    const [flipped, setFlipped] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const buttonRef = useRef<HTMLButtonElement>(null);
    const optionsElRef = useRef<HTMLElement | null>(null);

    const detectPosition = useCallback(() => {
        if (!optionsElRef.current || !buttonRef.current) return;
        const btnRect = buttonRef.current.getBoundingClientRect();
        const optRect = optionsElRef.current.getBoundingClientRect();
        setFlipped(optRect.bottom <= btnRect.top + 4);
    }, []);

    const optionsCallbackRef = useCallback(
        (el: HTMLElement | null) => {
            optionsElRef.current = el;
            if (el) requestAnimationFrame(detectPosition);
        },
        [detectPosition]
    );

    useEffect(() => {
        if (!searchable || !optionsElRef.current) return;
        const onReposition = () => requestAnimationFrame(detectPosition);
        window.addEventListener("scroll", onReposition, true);
        window.addEventListener("resize", onReposition);
        return () => {
            window.removeEventListener("scroll", onReposition, true);
            window.removeEventListener("resize", onReposition);
        };
    }, [detectPosition]);

    const filteredOptions = useMemo(() => {
        if (!searchable || !searchQuery.trim()) return options;
        const q = searchQuery.toLowerCase().trim();
        return options.filter(
            (option) =>
                option.name.toLowerCase().includes(q) ||
                option.description?.toLowerCase().includes(q)
        );
    }, [options, searchQuery, searchable]);

    const openBelow = !flipped;

    const handleChange = (option: SelectOption) => {
        setSearchQuery("");
        onChange(option);
    };

    const buttonBgClass = classButton || "bg-b-surface2";

    if (loading) {
        return (
            <div className={`${innerLabel ? "pt-1.5" : ""} ${className || ""}`}>
                {label && !innerLabel && (
                    <div className="flex items-center mb-4">
                        <div className="text-button">{label}</div>
                        {tooltip && (
                            <Tooltip className="ml-1.5" content={tooltip} />
                        )}
                    </div>
                )}
                <div className="relative" aria-busy="true" aria-label="Loading options">
                    {innerLabel && (
                        <div className={`absolute -top-2.5 left-6 px-1 text-caption pointer-events-none z-1 ${classLabel || "bg-b-surface2"}`}>
                            {innerLabel}
                        </div>
                    )}
                    <div
                        className={`flex h-12 w-full items-center justify-between rounded-3xl border border-s-stroke2 ${innerLabel ? "pl-6.5" : "pl-4.5"} pr-3 ${buttonBgClass}`}
                    >
                        <div className="h-3.5 w-[42%] max-w-48 animate-pulse rounded-full bg-s-stroke2" />
                        <div className="size-4 shrink-0 animate-pulse rounded-full bg-s-stroke2/80" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Listbox
            className={`${innerLabel ? "pt-1.5" : ""} ${className || ""}`}
            value={value ?? undefined}
            onChange={handleChange}
            disabled={disabled}
            as="div"
        >
            {({ open }) => (
                    <>
                        {label && !innerLabel && (
                            <Label className="flex items-center mb-4">
                                <div className="text-button">{label}</div>
                                {tooltip && (
                                    <Tooltip className="ml-1.5" content={tooltip} />
                                )}
                            </Label>
                        )}
                        <div className="relative">
                            {innerLabel && (
                                <div className={`absolute -top-2.5 left-6 px-1 text-caption pointer-events-none z-1 ${classLabel || "bg-b-surface2"}`}>
                                    {innerLabel}
                                </div>
                            )}
                            <ListboxButton
                                ref={buttonRef}
                                disabled={disabled}
                                className={`group flex justify-between items-center w-full h-12 pr-3 border border-s-stroke2 rounded-3xl text-body-2 text-t-primary fill-t-secondary outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50 ${!open && !disabled ? "data-[hover]:border-s-highlight data-[hover]:text-t-primary" : ""} ${innerLabel ? "pl-6.5" : "pl-4.5"} ${isBlack
                                    ? "bg-linear-to-b border-transparent from-[#2C2C2C] to-[#282828] !text-t-light !fill-t-light dark:from-shade-10 dark:to-[#DEDEDE]"
                                    : ""
                                    } ${open && openBelow
                                        ? "!rounded-b-none !border-s-subtle !border-b-transparent text-t-primary"
                                        : ""
                                    } ${open && !openBelow
                                        ? "!rounded-t-none !border-s-subtle !border-t-transparent text-t-primary"
                                        : ""
                                    } ${buttonBgClass}`}
                            >
                                {value?.name ? (
                                    <div className="flex items-center gap-2.5 truncate">
                                        {value.icon && <div className="shrink-0 flex items-center">{value.icon}</div>}
                                        <span className="truncate">{value.name}</span>
                                    </div>
                                ) : (
                                    <div className="truncate text-t-secondary/50">
                                        {placeholder}
                                    </div>
                                )}
                                <Icon
                                    className={`shrink-0 ml-2 fill-inherit transition-transform ${open && openBelow ? "rotate-180" : ""}`}
                                    name="chevron"
                                />
                            </ListboxButton>
                            <ListboxOptions
                                ref={optionsCallbackRef}
                                className={`z-100 [--anchor-gap:-2px] w-[var(--button-width)] bg-b-surface2 border border-s-subtle origin-top transition duration-200 ease-out outline-none data-[closed]:scale-95 data-[closed]:opacity-0 ${openBelow
                                    ? "shadow-depth border-t-0 rounded-b-[1.25rem]"
                                    : "shadow-depth-flipped border-b-0 rounded-t-[1.25rem]"
                                    } ${isBlack ? "!border-[#2C2C2C]" : ""
                                    } ${searchable ? "px-0 pb-0" : "px-2.25 pb-2.25"}`}
                                anchor="bottom"
                                transition
                            >
                                {searchable && (
                                    <div
                                        className={`sticky top-0 z-1 border-b border-s-subtle bg-b-surface2 px-2.25 py-2 ${openBelow ? "rounded-t-none" : "rounded-t-[1.25rem]"}`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="relative">
                                            <Icon
                                                className="pointer-events-none absolute left-3 top-1/2 !size-4 -translate-y-1/2 fill-t-tertiary"
                                                name="search"
                                            />
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                placeholder={searchPlaceholder}
                                                className="h-10 w-full rounded-full border border-s-stroke2 bg-b-surface2 pl-9 pr-3 text-caption text-t-primary outline-none transition-colors placeholder:text-t-tertiary hover:border-s-highlight focus:border-s-highlight"
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className={`max-h-60 overflow-y-auto ${searchable ? "px-2.25 pb-2.25 pt-1" : ""} [scrollbar-width:thin]`}>
                                    {filteredOptions.length === 0 ? (
                                        <div className="px-2.25 py-3 text-center text-caption text-t-tertiary">
                                            {emptyMessage}
                                        </div>
                                    ) : (
                                        filteredOptions.map((option) => (
                                            <ListboxOption
                                                className="relative pl-2.25 py-2 pr-5.5 rounded-lg text-body-2 text-t-secondary cursor-pointer transition-colors after:absolute after:top-1/2 after:right-2.5 after:size-2 after:-translate-y-1/2 after:rounded-full after:bg-t-blue after:opacity-0 after:transition-opacity data-[focus]:text-t-primary data-[selected]:bg-shade-08/50 data-[selected]:text-t-primary data-[selected]:after:opacity-100 dark:data-[selected]:bg-shade-06/10"
                                                key={option.id}
                                                value={option}
                                            >
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    {option.icon && <div className="shrink-0 flex items-center">{option.icon}</div>}
                                                    <div className="min-w-0">
                                                        <div className="truncate">{option.name}</div>
                                                        {option.description && (
                                                            <div className="truncate text-caption text-t-tertiary">
                                                                {option.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </ListboxOption>
                                        ))
                                    )}
                                </div>
                            </ListboxOptions>
                        </div>
                    </>
            )}
        </Listbox>
    );
};

export default Select;
